import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import exceljs from 'exceljs'

import type { Attribute } from './attribute'
import { Cell } from './cell'
import { NumberFormat } from './number-format'
import { Row } from './row'
import { convertValueType } from './value-type'

const TABULAR_DATA_BACKGROUND_COLOR = '203764'
const TABULAR_DATA_FONT_COLOR = 'FFFFFF'

type AttributeWithValueExtractor<TItem> = Readonly<
  Attribute & ents.core.common.Attribute.ValueExtractor<TItem>
>

/**
 * Represents a set of rows in the formation of a typical Excel data table: a
 * formatted header row at the top, followed by data rows
 */
export class Table<TItem> {
  public static readonly DEFAULT_COLUMN_WIDTH = 20

  public get rows(): Row[] {
    return [this.headerRow, ...this.dataRows]
  }

  public readonly headerRow: Row
  public readonly dataRows: Row[]

  constructor(
    private readonly _attributes: readonly AttributeWithValueExtractor<TItem>[],
    items: readonly TItem[],
  ) {
    this.headerRow = _getHeaderRow(_attributes)
    this.dataRows = _getDataRows(_attributes, items)
  }

  public addData(...items: readonly TItem[]): void {
    this.dataRows.push(..._getDataRows(this._attributes, items))
  }
}

/**
 * Generates a header row for tabular data
 */
function _getHeaderRow(attributes: readonly Attribute[]): Row {
  return new Row(
    attributes.map(({ name }) =>
      new Cell(name)
        .fontBold()
        .solidBackground(TABULAR_DATA_BACKGROUND_COLOR)
        .fontColor(TABULAR_DATA_FONT_COLOR),
    ),
  )
}

/**
 * Generates rows to represent given rows of tabular data
 */
function _getDataRows<TItem>(
  attributes: readonly AttributeWithValueExtractor<TItem>[],
  data: readonly TItem[],
): Row[] {
  const rows: Row[] = []
  for (const item of data) {
    rows.push(
      new Row(
        // Each attribute maps to a cell
        attributes.map(attribute => {
          // Cell with the value of this attribute
          const cell = new Cell(
            _coerceValidCellValue(attribute.getValueFrom(item), attribute),
          )
          if (TypeChecker.isNotNullish(cell.value)) {
            // Sets value, if the cell is not empty
            cell.setValueType(convertValueType(attribute.valueInfo?.type))
          }
          // Sets number format, if appropriate
          cell.setNumberFormat(
            attribute.numberFormat ?? NumberFormat.from(attribute.valueInfo),
          )
          return cell
        }),
      ),
    )
  }
  return rows
}

/**
 * Coerces a valid {@link exceljs.CellValue} from the given value
 *
 * For example, a list is not a valid {@link exceljs.CellValue}, so we need to
 * stringify any lists. This applies to a variety of invalid data types.
 */
function _coerceValidCellValue(
  value: unknown,
  attribute: Readonly<ents.core.common.Attribute>,
): exceljs.CellValue {
  const type = attribute.valueInfo?.type

  // (1) We check if this is a type that can be presented in a special way in
  // Excel

  if (type === ents.core.common.value.types.Intrinsic.Date) {
    return ents.core.display.date.normalizeToUtc(value)
  }

  if (
    type === ents.core.common.value.types.Intrinsic.Url &&
    typeof value === 'string'
  ) {
    // URLs can be turned into hyperlinks
    return { hyperlink: value, text: value }
  }

  // (2) If not handled above, we check if this is at least a valid type as is

  if (isExcelCellValue(value)) {
    // If it is already a valid value, we just return it
    return value
  }

  // (3) If it is not a valid type, we try and coerce into the most
  // user-friendly, valid representation of the data that we can

  if (TypeChecker.isReadonlyArray(value)) {
    // For lists, we use comma-separated strings
    return value.map(subvalue => JSON.stringify(subvalue)).join(',')
  }

  // Everything else, just gets turned into a string
  return value?.toString()
}

/**
 * Used in {@link _coerceValidCellValue}
 */
const isExcelCellValue: TypeChecker<exceljs.CellValue> =
  TypeChecker.Factory.union(
    TypeChecker.isNull,
    TypeChecker.isUndefined,
    TypeChecker.isNumber,
    TypeChecker.isString,
    TypeChecker.isBoolean,
    TypeChecker.isDate,
    // NOTE: for each of the below, we just want to check enough to know that it
    // is of one of these types. We don't need to check every field and subfield
    TypeChecker.Factory.object({
      error: TypeChecker.Factory.oneOf(
        // NOTE: for some reason, CellErrorValue is typed using string literals
        // rather than the enum. So we need to cast the enum to string literals
        Object.values(
          exceljs.ErrorValue,
        ) satisfies exceljs.ErrorValue[] as `${exceljs.ErrorValue}`[],
      ),
    }) satisfies TypeChecker<exceljs.CellErrorValue>,
    TypeChecker.Factory.object({
      // NOTE: we only check for formula because if that exists, then that should
      // be enough to indicate that this is a cell formula value
      formula: TypeChecker.isString,
    }) satisfies TypeChecker<exceljs.CellFormulaValue> as TypeChecker<exceljs.CellFormulaValue>,
    TypeChecker.Factory.object({
      // NOTE: we only check for sharedFormula because if that exists, then that should
      // be enough to indicate that this is a cell shared formula value
      sharedFormula: TypeChecker.isString,
    }) satisfies TypeChecker<exceljs.CellSharedFormulaValue> as TypeChecker<exceljs.CellSharedFormulaValue>,
    TypeChecker.Factory.object({
      // NOTE: just these two should be enough to indicate a hyperlink
      hyperlink: TypeChecker.isString,
      text: TypeChecker.isString,
    }) satisfies TypeChecker<exceljs.CellHyperlinkValue>,
    TypeChecker.Factory.object({
      // NOTE: we don't care if every subfield is exactly correct. This is close enough
      richText: TypeChecker.isArray,
    }) satisfies TypeChecker<exceljs.CellRichTextValue>,
  ) satisfies TypeChecker<exceljs.CellValue>
